import { useQuery, UseQueryOptions } from '@tanstack/react-query'
import { AxiosError } from 'axios'
import query from '@/api/APIServicesV2'
import {
  ContainersProductsResponse,
  InventoryItem,
} from '../interfaces/inventory'

export interface ContainersProductsQueryParams {
  page?: number
  limit?: number
  search?: string // Parámetro general de búsqueda
  productName?: string // Parámetro específico para búsqueda por nombre de producto
  name?: string // Otro parámetro posible para nombres
  q?: string // Parámetro de query general
  supplierIds?: string // Filtro por proveedor (IDs separados por coma)
  expiration?: string // Filtro por vencimiento (urgent, alert, normal, etc.)
}

// Create a unique, descriptive key for this query
const CONTAINERS_PRODUCTS_QUERY_KEY = 'containers-products'

/**
 * Fetch containers products from the API
 * Separating API call logic from the hook for better testability
 */
const fetchContainersProducts = async (
  params?: ContainersProductsQueryParams
): Promise<ContainersProductsResponse> => {
  // Filter out null/undefined values to clean up the request
  const cleanParams = Object.fromEntries(
    Object.entries(params || {}).filter(([, value]) => value != null)
  )

  const response = await query.get('/containers/products', {
    params: cleanParams,
  })

  return response.data
}

/**
 * Transform API response to match current table interface
 */
const transformInventoryData = (
  apiData: ContainersProductsResponse
): InventoryItem[] => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  return apiData.items.map((item, _index) => {
    // Para fechas de vencimiento, usar la fecha más próxima como principal
    const sortedExpirations = [...item.expirations].sort(
      (a, b) => new Date(a).getTime() - new Date(b).getTime()
    )
    const earliestExpiration = sortedExpirations[0] || ''

    return {
      // Campos originales de la API
      productId: item.productId,
      productName: item.productName,
      productImageUrl: item.productImageUrl,
      quantity: item.quantity,
      containers: item.containers,
      suppliers: item.suppliers,
      warehouses: item.warehouses,
      expirations: item.expirations,

      // Campos transformados para compatibilidad con la tabla actual
      id: item.productId.toString(),
      name: item.productName, // CLAVE: este debería ser el nombre del producto
      totalQuantity: item.quantity.availableQuantity,
      containersCount: item.containers.length,
      suppliersNames: item.suppliers.map((s) => s.name),
      expirationDate: earliestExpiration,
      // Generar fechas simuladas basadas en el productId para que sean diferentes
      createdAt: new Date(Date.now() - item.productId * 86400000).toISOString(), // Días atrás según productId
      updatedAt: new Date(Date.now() - item.productId * 3600000).toISOString(), // Horas atrás según productId
      imageUrl: item.productImageUrl || undefined,
      lotCount: item.expirations.length,
    }
  })
}

/**
 * Custom hook for fetching containers products with filtering
 *
 * @param params - Query parameters for filtering containers products
 * @param options - TanStack Query options for customizing query behavior
 * @returns Query result object containing data, loading states, and utility functions
 */
export const useContainersProductsQuery = ({
  params = {},
  options,
}: {
  params?: ContainersProductsQueryParams
  options?: Partial<UseQueryOptions<ContainersProductsResponse, AxiosError>>
} = {}) => {
  // Normalize params with defaults
  const normalizedParams: ContainersProductsQueryParams = {
    page: 1,
    limit: 20,
    ...params,
  }

  // Query hook con cache optimizado para búsquedas
  const queryResult = useQuery<ContainersProductsResponse, AxiosError>({
    // Create a structured query key that includes all parameters
    // El search se incluye en la key para cache independiente por búsqueda
    queryKey: [CONTAINERS_PRODUCTS_QUERY_KEY, normalizedParams],

    queryFn: () => {
      return fetchContainersProducts(normalizedParams)
    },

    // Configuración de cache optimizada para búsquedas
    refetchOnWindowFocus: false,
    staleTime: 10 * 60 * 1000, // 10 minutos - cache más largo para búsquedas
    gcTime: 30 * 60 * 1000, // 30 minutos - mantener en cache más tiempo

    // Placeholders para mejorar UX durante cambios de búsqueda
    placeholderData: (previousData) => {
      // Si hay datos previos y solo cambió la búsqueda, mantener datos anteriores como placeholder
      if (previousData && normalizedParams.search !== undefined) {
        return previousData
      }
      return undefined
    },

    // Apply any custom options
    ...options,
  })

  // Transform data for compatibility with existing components
  const transformedData = queryResult.data
    ? transformInventoryData(queryResult.data)
    : []

  return {
    inventoryData: transformedData,
    pagination: {
      page: queryResult.data?.page || 1,
      limit: queryResult.data?.limit || 20,
      total: queryResult.data?.total || 0,
    },
    isLoading: queryResult.isLoading,
    isFetching: queryResult.isFetching || queryResult.isRefetching,
    isError: queryResult.isError,
    error: queryResult.error,
    refetch: queryResult.refetch,
  }
}

/**
 * Helper function to get the containers products query key
 * Useful for cache invalidation or prefetching
 */
export const getContainersProductsQueryKey = (
  params: ContainersProductsQueryParams
) => [CONTAINERS_PRODUCTS_QUERY_KEY, params]
